/*
 * Banner contract — GET /api/site/banners/
 * ========================================
 *
 * This mirrors the API response verbatim. Property names, types and nullability
 * come straight from the backend contract and must not be renamed, reshaped or
 * "normalised" into something else: the frontend adapts to the API, never the
 * other way round.
 *
 * Portable on purpose — Treasure / Patek / Richard Mille reuse this file and
 * `useBanners` unchanged; only the positioning/styling layer is site specific.
 */

export interface Banner {
    id: number
    /** Desktop image URL. */
    image_url: string
    /** Mobile image URL, or null when the desktop image should be reused. */
    mobile_image_url: string | null
    show_on_mobile: boolean
    /** Used verbatim as the <img alt>. */
    alt_text: string
    /** Destination URL. Absent / empty means the banner is not a link. */
    link_url: string | null
    open_in_new_tab: boolean
}

export interface BannerSet {
    main: Banner[]
    left: Banner[]
    right: Banner[]
    /** Sticky for both side rails; missing API values default to false. */
    sticky: boolean
}

export type BannerSlot = 'main' | 'left' | 'right'

export const EMPTY_BANNER_SET: BannerSet = {
    main: [], left: [], right: [], sticky: false,
}

/** A banner is clickable only when link_url actually carries a value. */
export function bannerHref(banner: Banner): string | undefined {
    const link = banner.link_url
    if (typeof link !== 'string') return undefined
    const trimmed = link.trim()
    return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Mobile source rule, clarified by Jake: `mobile_image_url ?? image_url`.
 * Desktop always uses `image_url`.
 */
export function bannerSrc(banner: Banner, mobile: boolean): string {
    if (mobile && banner.mobile_image_url) return banner.mobile_image_url
    return banner.image_url
}
